import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, ArrowRight, AlertCircle, Shield, MessageSquare,
  ArrowLeft, RefreshCw, CheckCircle, Loader2, Lock, Eye, EyeOff, Phone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { OFFICIAL_SEAL } from '@/lib/brand';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Password-first + 2FA admin gateway.
// Flow:
//   credentials -> method -> verify -> success
// Backend endpoints:
//   POST /api/admin/auth/password-verify  (validate password, get session_id)
//   POST /api/admin/auth/send-2fa         (send code via email or sms)
//   POST /api/admin/auth/verify-2fa       (validate code, issue admin JWT)
export default function AdminLoginPage() {
  const [step, setStep] = useState('credentials'); // credentials | method | verify | success
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [methods, setMethods] = useState([]);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [chosenMethod, setChosenMethod] = useState('email');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [info, setInfo] = useState('');
  const [countdown, setCountdown] = useState(0);

  const otpRefs = useRef([]);
  const navigate = useNavigate();
  const { checkAuth, user } = useAuth();

  // Bounce if already authenticated as admin
  useEffect(() => {
    if (user && user.portal_role === 'admin') {
      navigate('/internal/admin/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Resend cooldown
  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  // ───────────────────────────── Step 1: validate password
  const handlePasswordVerify = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setInfo('');
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/auth/password-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Password verification failed.');
      }
      setSessionId(data.session_id);
      setMethods(data.methods || ['email']);
      setMaskedEmail(data.masked_email || '');
      setMaskedPhone(data.masked_phone || '');
      setChosenMethod((data.methods || ['email'])[0] || 'email');
      setStep('method');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ───────────────────────────── Step 2: send code via chosen method
  const handleSendCode = async (method) => {
    setError(''); setSuccess(''); setInfo('');
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/auth/send-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, method }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to send verification code.');
      }
      if (method === 'sms' && data.success === false) {
        // Graceful "not configured" handling per spec — stay on method picker
        setInfo(data.message || 'SMS verification is not yet configured. Please use Email verification.');
        setChosenMethod('email');
        return;
      }
      // Email path — transition to verify regardless of best-effort delivery flag
      setSuccess(data.message || `Verification code sent.`);
      setStep('verify');
      setCountdown(45);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ───────────────────────────── Step 3: verify code
  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
    if (next.every(d => d !== '') && next.join('').length === 6) {
      setTimeout(() => handleVerifyOTP(next.join('')), 100);
    }
  };
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };
  const handleOtpPaste = (e) => {
    const txt = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (txt.length === 6) {
      e.preventDefault();
      const next = txt.split('');
      setOtp(next);
      otpRefs.current[5]?.focus();
      setTimeout(() => handleVerifyOTP(txt), 100);
    }
  };

  const handleVerifyOTP = async (codeOverride) => {
    const code = (codeOverride || otp.join('')).trim();
    if (code.length !== 6) {
      setError('Please enter the 6-digit code.');
      return;
    }
    setError(''); setSuccess(''); setInfo('');
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/auth/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Verification failed.');
      }
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('portal_role', data.user?.portal_role || 'admin');
      await checkAuth();
      setStep('success');
      setSuccess('Authenticated. Redirecting to Admin Portal…');
      setTimeout(() => navigate('/internal/admin/dashboard', { replace: true }), 700);
    } catch (err) {
      setError(err.message);
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  // ───────────────────────────── Render
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card className="border-slate-700 bg-slate-800/50 backdrop-blur-sm shadow-2xl">
          <CardContent className="p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <img
                src={OFFICIAL_SEAL}
                alt="Zodiac Workforce Intelligence Institute"
                className="w-24 h-24 mx-auto mb-3 object-contain drop-shadow-[0_6px_20px_rgba(200,161,87,0.35)]"
                data-testid="admin-login-seal"
              />
              <div className="zodiac-heading text-white font-bold text-2xl tracking-tight leading-none">ZODIAC</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C8A157] font-semibold mt-1">
                Workforce Intelligence Institute<sup className="text-[7px] ml-0.5 align-super">™</sup>
              </div>
              <h1 className="text-xl font-bold text-white mt-4">Admin Portal</h1>
              <p className="text-xs text-slate-400 mt-1">
                {step === 'credentials' && 'Password-first authentication with 2-factor verification.'}
                {step === 'method' && 'Choose how to receive your verification code.'}
                {step === 'verify' && 'Enter the 6-digit verification code.'}
                {step === 'success' && 'Sign-in complete.'}
              </p>
            </div>

            {/* Alerts */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-2"
                data-testid="admin-error"
              >
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-400">{error}</p>
              </motion.div>
            )}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2"
                data-testid="admin-success"
              >
                <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <p className="text-sm text-emerald-400">{success}</p>
              </motion.div>
            )}
            {info && (
              <motion.div
                initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center gap-2"
                data-testid="admin-info"
              >
                <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <p className="text-sm text-amber-300">{info}</p>
              </motion.div>
            )}

            <AnimatePresence mode="wait">
              {/* STEP 1 — Credentials */}
              {step === 'credentials' && (
                <motion.form
                  key="credentials"
                  initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }}
                  onSubmit={handlePasswordVerify} className="space-y-6"
                >
                  <div className="space-y-2">
                    <Label className="text-slate-300">Administrator Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input
                        type="email"
                        placeholder="admin@zodiacinstitute.org"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-purple-500"
                        required
                        data-testid="admin-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-purple-500"
                        required
                        data-testid="admin-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500">
                      A verification code will be required after your password is validated.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white h-12"
                    disabled={isLoading}
                    data-testid="admin-continue-btn"
                  >
                    {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                      <>Continue<ArrowRight className="w-5 h-5 ml-2" /></>
                    )}
                  </Button>
                </motion.form>
              )}

              {/* STEP 2 — Method picker */}
              {step === 'method' && (
                <motion.div
                  key="method"
                  initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }}
                  className="space-y-4"
                >
                  <p className="text-sm text-slate-300">
                    Password verified. Select a delivery method for your verification code:
                  </p>

                  <button
                    type="button"
                    onClick={() => { setChosenMethod('email'); handleSendCode('email'); }}
                    disabled={isLoading}
                    className="w-full flex items-center justify-between p-4 rounded-lg bg-slate-700/40 hover:bg-slate-700/70 border border-slate-600 text-left transition-colors disabled:opacity-50"
                    data-testid="admin-method-email"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-600/20 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-purple-300" />
                      </div>
                      <div>
                        <div className="text-white font-semibold">Send by Email</div>
                        <div className="text-xs text-slate-400">{maskedEmail || 'Your registered email'}</div>
                      </div>
                    </div>
                    {isLoading && chosenMethod === 'email'
                      ? <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
                      : <ArrowRight className="w-5 h-5 text-slate-400" />}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setChosenMethod('sms'); handleSendCode('sms'); }}
                    disabled={isLoading}
                    className="w-full flex items-center justify-between p-4 rounded-lg bg-slate-700/40 hover:bg-slate-700/70 border border-slate-600 text-left transition-colors disabled:opacity-50"
                    data-testid="admin-method-sms"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-emerald-300" />
                      </div>
                      <div>
                        <div className="text-white font-semibold">Send by SMS/Text</div>
                        <div className="text-xs text-slate-400">{maskedPhone || 'Not yet configured'}</div>
                      </div>
                    </div>
                    {isLoading && chosenMethod === 'sms'
                      ? <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
                      : <Phone className="w-5 h-5 text-slate-400" />}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setStep('credentials'); setError(''); setInfo(''); setSuccess(''); }}
                    className="text-slate-400 hover:text-white text-sm flex items-center gap-1 pt-2"
                    data-testid="admin-back-to-credentials"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                </motion.div>
              )}

              {/* STEP 3 — Verify code */}
              {step === 'verify' && (
                <motion.div
                  key="verify"
                  initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }}
                  className="space-y-6"
                  data-testid="admin-verify-step"
                >
                  <div className="text-center">
                    <Shield className="w-12 h-12 mx-auto text-purple-400 mb-3" />
                    <p className="text-slate-300 mb-1">Enter Verification Code</p>
                    <p className="text-sm text-slate-500">
                      Sent to {chosenMethod === 'email' ? maskedEmail : maskedPhone}
                    </p>
                  </div>

                  <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
                    {otp.map((digit, index) => (
                      <Input
                        key={index}
                        ref={el => otpRefs.current[index] = el}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className="w-12 h-14 text-center text-2xl font-bold bg-slate-700/50 border-slate-600 text-white focus:border-purple-500"
                        data-testid={`admin-otp-input-${index}`}
                      />
                    ))}
                  </div>

                  <Button
                    onClick={() => handleVerifyOTP()}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white h-12"
                    disabled={isLoading || otp.join('').length !== 6}
                    data-testid="admin-verify-btn"
                  >
                    {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                      <>Verify & Sign In<ArrowRight className="w-5 h-5 ml-2" /></>
                    )}
                  </Button>

                  <div className="flex items-center justify-between text-sm">
                    <button
                      type="button"
                      onClick={() => { setStep('method'); setOtp(['','','','','','']); setError(''); setSuccess(''); }}
                      className="text-slate-400 hover:text-white flex items-center gap-1"
                      data-testid="admin-back-to-method"
                    >
                      <ArrowLeft className="w-4 h-4" /> Change method
                    </button>
                    <button
                      type="button"
                      onClick={() => countdown > 0 ? null : handleSendCode(chosenMethod)}
                      disabled={countdown > 0 || isLoading}
                      className="text-purple-400 hover:text-purple-300 flex items-center gap-1 disabled:text-slate-600"
                      data-testid="admin-resend-btn"
                    >
                      <RefreshCw className="w-4 h-4" />
                      {countdown > 0 ? `Resend in ${countdown}s` : 'Resend code'}
                    </button>
                  </div>
                </motion.div>
              )}

              {/* STEP 4 — Success splash */}
              {step === 'success' && (
                <motion.div
                  key="success"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="text-center py-6"
                >
                  <CheckCircle className="w-14 h-14 mx-auto text-emerald-400 mb-3" />
                  <p className="text-white font-semibold">Welcome back, Administrator.</p>
                  <p className="text-slate-400 text-sm mt-1">Redirecting to the Admin Portal…</p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-slate-700 text-center">
              <p className="text-xs text-slate-500">
                Authorized personnel only. All access is logged.
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
